// Copyright (c) StrangeBee 2024

import { useLocalDico, useLocalMessage } from '@Helpers/locale';
import { ObjectTypeV1 as ObjectType, AttackPatternsOutput, AdminPermissionsEnum, CatalogOutput } from 'thehive-sdk';
import React, { ReactElement, useMemo } from 'react';
import client from '@Client';
import { StorageFilterEnum } from '@Hooks/useStoragePage';
import { DisplayTags } from '@Atoms/Display';
import { PreviewButton } from '@Atoms/Buttons';
import THDrawer from '@Molecules/THDrawer/THDrawer';
import Details from '@Molecules/Details/Details';
import { truncate } from '@Helpers/';
import useOneQuery, { ExtractDataQuery } from '@Hooks/useOneQuery';
import { useExtractData } from './configData';
import ExternalLink from '@Atoms/ExternalLink/ExternalLink';
import { Space } from 'antd';
import { ICONS } from '@Constants/iconsApp';
import RouteBuilder from '@Services/RouteBuilder';
import { RouteType } from '@Types';
import useConfigList from '@Hooks/useConfigList';
import { THFilter } from '@Molecules/THFilter/THFilter';
import { THList } from '@Molecules/THList';
import { useListAllFields } from '@Queries/describeQueries';
import { Footer } from '@Organisms/ExtendFilterFooter/ExtendFilterFooter';
import { PaginationBrowse } from '@Atoms/Inputs/PaginationBrowse/PaginationBrowse';
import { PaginationSize } from '@Atoms/Inputs/PaginationSize/PaginationSize';
import { ManageSwitch } from '@Molecules/ManageSwitch/ManageSwitch';
import { DisplayEmptyData } from '@Atoms/Display/DisplayEmptyData/DisplayEmptyData';
import { useParams } from 'react-router-dom';
import { computeGetSelector } from '@Organisms/AttackPatterns/selector';
import { useRouteMatch } from 'react-router-dom';
import { ManageViewList } from '@Molecules/ManageViewList/ManageViewList';
import usePageTitle from '@Hooks/usePageTitle';
import { markdownSanitize } from '../markdownSanitize';

import './AttackPatterns.scss';

const AttackPatternsList = (): ReactElement => {
    const i = useLocalMessage();

    const { catalogId } = useParams<{ catalogId: string }>();

    const config = useConfigList<AttackPatternsOutput>(
        useMemo(
            () => ({
                selector: {
                    head: [{ name: 'getCatalog', idOrName: catalogId }, { name: 'patterns' }],
                    name: `get-all-pattern-${catalogId}`,
                    sort: ['+name'],
                    extraData: ['enabled', 'parent'],
                },
                controller: client.AttackPatternsController,
                storageId: StorageFilterEnum.ATTACK_PATTERNS,
                objectType: ObjectType.ATTACK_PATTERNS,
            }),
            [catalogId],
        ),
    );

    const { data: catalogData } = useOneQuery<CatalogOutput>({
        selector: computeGetSelector({ catalogId }),
        controller: client.AttackPatternsController,
    });

    const {
        dataSource = [],
        columnsRenderer,
        status = 'loading',
        table,
        filterManager,
        paginationManager,
        storageViewManager,
        storagePageManager,
    } = config;

    const columsTrad = useLocalDico(
        [
            'attackPattern.list.columns.patternId',
            'attackPattern.list.columns.name',
            'attackPattern.list.columns.parent-patternId',
            'attackPattern.list.columns.tactics',
            'attackPattern.list.columns.description',
            'common.preview',
            'attackPattern.preview.name',
            'attackPattern.preview.nameParent',
            'attackPattern.preview.url',
            'attackPattern.preview.dataSources',
            'attackPattern.preview.defenseBypassed',
            'attackPattern.preview.permissionsRequired',
            'attackPattern.preview.systemRequirements',
            'attackPattern.preview.remoteSupport',
            'attackPattern.preview.capecId',
            'attackPattern.preview.capecUrl',
            'attackPattern.preview.description',
            'attackPattern.preview.children',
            'attackPattern.preview.patternId',
            'attackPattern.preview.tactics',
        ],
        i,
    );
    const extractData = useExtractData(columsTrad);

    const columns = columnsRenderer([
        {
            title: columsTrad['attackPattern.list.columns.patternId'],
            className: 'pattern___row-patternId',
            render: ({ patternId }): ReactElement => <div>{patternId}</div>,
        },
        {
            title: columsTrad['attackPattern.list.columns.name'],
            className: 'pattern___row-name column_preview',
            render: (attack): ReactElement => (
                <div className="user-title">
                    {attack.name}
                    <div className="generic-drawer-preview">
                        <THDrawer
                            title={`${attack.patternId} - ${attack.name}`}
                            triggerElem={<PreviewButton label={columsTrad['common.preview']} />}
                        >
                            <ExtractDataQuery
                                selector={{
                                    head: [{ name: 'getPattern', idOrName: attack.patternId }],
                                    name: `get-pattern-${attack.patternId}`,
                                    extraData: ['children', 'parent'],
                                }}
                                controller={client.AttackPatternsController}
                            >
                                {(data: any): ReactElement => <Details data={data} extract={extractData} />}
                            </ExtractDataQuery>
                        </THDrawer>
                    </div>
                </div>
            ),
        },
        {
            title: columsTrad['attackPattern.list.columns.parent-patternId'],
            className: 'pattern___row-parent',
            render: ({ extraData }, { custom }): ReactElement => (
                <>
                    {extraData?.parent?.patternId ? (
                        <Space>
                            <ExternalLink url={extraData?.parent?.url}>{extraData?.parent?.patternId}</ExternalLink>
                            <ICONS.FilterTwoTone
                                onClick={(): void =>
                                    custom({ field: 'parent', values: extraData?.parent?.patternId, operator: 'any' })
                                }
                            />
                        </Space>
                    ) : null}
                </>
            ),
        },
        {
            title: columsTrad['attackPattern.list.columns.tactics'],
            className: 'pattern___row-tactics',
            render: ({ tactics }, { custom }): ReactElement =>
                tactics && tactics.length !== 0 ? (
                    <DisplayTags
                        tags={tactics}
                        className="pattern__tactics-list"
                        onClick={(item): void => custom({ field: 'tactics', values: item, operator: 'any' })}
                    />
                ) : (
                    <i>--</i>
                ),
        },
        {
            title: columsTrad['attackPattern.list.columns.description'],
            className: 'pattern___row-description',
            render: ({ description }): ReactElement => (
                <div
                    dangerouslySetInnerHTML={{
                        __html: truncate(markdownSanitize(description ?? '')),
                    }}
                />
            ),
        },
    ]);

    const { data: describe } = useListAllFields();

    usePageTitle(`${i('page-title.admin.entities.attack-patterns')} - ${catalogData?.name}`);

    return (
        <THList>
            <THList.Header>
                <THList.Slot name="MANAGE">
                    {storagePageManager && storageViewManager ? (
                        <ManageViewList
                            pageManager={storagePageManager}
                            viewManager={storageViewManager}
                            beforeCreate={(content: any): any => {
                                filterManager.applyFilters();
                                return { ...content, filters: filterManager.tempFilters };
                            }}
                        />
                    ) : null}
                </THList.Slot>
                <THList.Slot name="NAVIGATION">
                    <ManageSwitch
                        label={i('common.filters')}
                        value={filterManager.editFilters}
                        onChange={(editFilters): void => {
                            filterManager.setEditFilters(editFilters);
                        }}
                        icon={<ICONS.FilterOutlined />}
                    />
                </THList.Slot>
                <THList.Slot name="CONTENT">
                    {(filterManager.editFilters || !!filterManager.tempFilters.length) && (
                        <THFilter filterConfig={filterManager} describeField={describe?.[ObjectType.ATTACK_PATTERNS]}>
                            <Footer filterConfig={filterManager} />
                        </THFilter>
                    )}
                </THList.Slot>
            </THList.Header>

            <THList.Content
                dataSource={dataSource}
                columns={columns}
                rowKey="_id"
                emptyPlaceholder={<DisplayEmptyData spin={status === 'loading'} label={i('attackPattern.none')} />}
            />

            <THList.Footer>
                <THList.Slot name="NAVIGATION">
                    <PaginationBrowse count={table.count} paginationConfig={paginationManager} />
                    <PaginationSize paginationConfig={paginationManager} />
                </THList.Slot>
            </THList.Footer>
        </THList>
    );
};

const path = RouteBuilder.compute('administration.catalogPatterns');

const BreadcrumbLabel = (): ReactElement => {
    const match = useRouteMatch(RouteBuilder.compute('administration.catalogPatterns'));

    const { catalogId = '' } = (match?.params as any) || {};

    const { data: catalogData } = useOneQuery<CatalogOutput>({
        selector: computeGetSelector({ catalogId }),
        controller: client.AttackPatternsController,
    });

    return <>{catalogData?.name}</>;
};

export const Route: RouteType = {
    path,
    content: <AttackPatternsList />,
    breadcrumb: {
        label: <BreadcrumbLabel />,
    },
    permissionNeeded: [AdminPermissionsEnum.MANAGE_PATTERN],
    space: ['admin'],
};
